import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import Button from '../Button/Button';
import Modal, { ModalBody, ModalFooter } from '../Modal';
import { Text } from 'rebass';
import { Textarea } from '@rebass/forms';
import { Input, Label } from '../Form';
import { useMutation } from '@apollo/client';
import { ContactProfileInput } from '../Profile/types';
import Form from '../Form/Form';
import FormRow from '../Form/FormRow';
import FormColumn from '../Form/FormColumn';
import useNotify from '../../hooks/useNotify';
import { TesterInput } from './types';
import Perm from '../../types/perm';
import ContactProfileForm from '../Profile/ContactProfileForm';
import {
  CREATE_BACKFLOW_TESTER,
  UPDATE_BACKFLOW_TESTER,
} from './TesterEditModal.queries';
import useMode from '../../hooks/useMode';

type TesterEditModalProps = {
  onClose: () => void;
  onSave?: (tester: TesterInput) => void;
  perms?: Perm[];
} & (
  | {
      action: 'add';
      tester?: Partial<TesterInput>;
    }
  | {
      action: 'edit';
      tester?: TesterInput;
      perms: Perm[];
    }
);

const TesterEditModal: React.FC<TesterEditModalProps> = ({
  onClose,
  onSave,
  action,
  tester,
  perms,
}) => {
  const mode = useMode();

  const [testerInput, setTesterInput] = useState<TesterInput>(
    tester
      ? {
          accountNumber: tester.accountNumber,
          status: tester.status || 'ACTIVE',
          notes: tester.notes || '',
          profile: tester.profile
            ? {
                type: tester.profile.type || 'INDIVIDUAL',
                name: tester.profile.name,
                firstName: tester.profile.firstName,
                lastName: tester.profile.lastName,
                phones: tester.profile.phones?.map((phone) => ({
                  subType: phone.subType,
                  value: phone.value,
                  extension: phone.extension,
                })),
                emails: tester.profile.emails?.map((email) => ({
                  subType: email.subType,
                  value: email.value,
                })),
                contactAddressId: tester.profile.contactAddressId,
                billingAddressId: tester.profile.billingAddressId,
              }
            : {
                type: 'INDIVIDUAL',
                name: '',
                firstName: '',
                lastName: '',
                phones: [],
                emails: [],
              },
          user: tester.user
            ? {
                email: tester.user.email,
                password: tester.user.password,
              }
            : {
                email: '',
                password: '',
              },
        }
      : {
          status: 'ACTIVE',
          notes: '',
          profile: {
            type: 'INDIVIDUAL',
            name: '',
            firstName: '',
            lastName: '',
            phones: [],
            emails: [],
          },
        }
  );

  const { showError, showSuccess } = useNotify();

  const [
    createTester,
    { data: createData, loading: createLoading, error: createError },
  ] = useMutation<{ createVendor: TesterInput }, { input: TesterInput }>(
    CREATE_BACKFLOW_TESTER,
    {
      onError: (error) => {
        showError('Error creating tester', error);
        console.error('Error creating tester', error);
      },
      onCompleted: (data) => {
        showSuccess('Tester added');
        if (data.createVendor && onSave) {
          onSave(data.createVendor);
        }
        onClose();
      },
    }
  );

  const [
    updateTester,
    { data: updateData, loading: updateLoading, error: updateError },
  ] = useMutation<
    { updateVendor: TesterInput },
    { vendorId: string; input: TesterInput }
  >(UPDATE_BACKFLOW_TESTER, {
    onError: (error) => {
      showError('Error updating tester', error);
      console.error('Error updating tester', error);
    },
    onCompleted: (data) => {
      showSuccess('Tester updated');
      if (data.updateVendor && onSave) {
        onSave(data.updateVendor);
      }
      onClose();
    },
  });

  const onSubmit = useCallback(() => {
    const input = { ...testerInput };
    if (input.user) {
      input.user = { ...input.user };
      if (input.user.password === '') {
        delete input.user.password;
      }
    }

    if (action === 'add') {
      createTester({
        variables: { input },
      });
    } else if (action === 'edit') {
      updateTester({
        variables: { vendorId: tester?.id as string, input },
      });
    }
  }, [testerInput, createTester, tester?.id, action, updateTester]);

  const handleTesterObjChange = (e: any) => {
    const { name, value } = e.target;
    setTesterInput((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const handleTesterLoginChange = (e: any) => {
    const { name, value } = e.target;
    setTesterInput((prevState) => ({
      ...prevState,
      user: {
        ...prevState?.user,
        [name]: value,
      },
    }));
  };

  const handleTesterProfileObjChange = (e: any) => {
    const { name, value } = e.target;

    setTesterInput((prevState) => ({
      ...prevState,
      profile: {
        ...prevState.profile,
        [name]: value,
      },
    }));
  };

  const onProfileChange = (profile: ContactProfileInput) => {
    setTesterInput((prevState) => ({
      ...prevState,
      profile: {
        ...prevState.profile,
        ...profile,
      },
    }));
  };

  const canEditProfile = action === 'add' || !!perms?.includes('UPDATE');
  const readOnly = action === 'edit' && !canEditProfile;

  return (
    <Modal
      title={action === 'add' ? 'Add Backflow Tester' : 'Edit Backflow Tester'}
      onClose={onClose}
    >
      <ModalBody>
        <Form>
          <FormRow>
            {!canEditProfile && (
              <FormColumn>
                <Label>Name</Label>
                {testerInput.profile.name}
              </FormColumn>
            )}
            {/*{action === 'add' && (*/}
            {/*  <FormColumn>*/}
            {/*    <Label>Type</Label>*/}
            {/*    <FormSelect*/}
            {/*      name="type"*/}
            {/*      onChange={handleTesterProfileObjChange}*/}
            {/*      value={testerInput.profile.type}*/}
            {/*      width="100%"*/}
            {/*    >*/}
            {/*      <option value="" disabled hidden>*/}
            {/*        Select Type*/}
            {/*      </option>*/}
            {/*      {profileTypes.map(({ value, label }) => (*/}
            {/*        <option key={value} value={value}>*/}
            {/*          {label}*/}
            {/*        </option>*/}
            {/*      ))}*/}
            {/*    </FormSelect>*/}
            {/*  </FormColumn>*/}
            {/*)}*/}
          </FormRow>
          {canEditProfile && (
            <ContactProfileForm
              onChange={onProfileChange}
              value={testerInput.profile}
              disabled={readOnly}
              show={['name']}
            />
          )}
          <FormRow width="calc(50% + 6px)" pr={3}>
            <FormColumn>
              <Label>Account Number</Label>
              <Input
                name="accountNumber"
                onChange={handleTesterObjChange}
                type="text"
                value={testerInput.accountNumber}
                width="100%"
                autoComplete="off"
                disabled={readOnly}
              />
            </FormColumn>
          </FormRow>
          {canEditProfile && (
            <ContactProfileForm
              onChange={onProfileChange}
              value={testerInput.profile}
              disabled={readOnly}
              show={['contactAddressId', 'emails', 'phones']}
            />
          )}
          <FormColumn>
            <Label>Notes</Label>
            <Textarea
              height={16 * 3 + 2}
              id="notes"
              name="notes"
              value={testerInput.notes || ''}
              onChange={handleTesterObjChange}
              disabled={readOnly}
            />
          </FormColumn>
          {canEditProfile && !readOnly && (
            <>
              <Text mt={5} variant="body1">
                {tester?.user?.email && 'Update User Login'}
                {!tester?.user?.email && 'Create User Login (Optional)'}
              </Text>
              <FormRow>
                <FormColumn>
                  <Label>Email</Label>
                  <Input
                    width="100%"
                    type="email"
                    value={testerInput?.user?.email || ''}
                    onChange={handleTesterLoginChange}
                    name="email"
                    autoComplete="off"
                  />
                </FormColumn>
                <FormColumn>
                  <Label>Password</Label>
                  <Input
                    width="100%"
                    type="password"
                    value={testerInput?.user?.password || ''}
                    onChange={handleTesterLoginChange}
                    name="password"
                    autoComplete="new-password"
                  />
                </FormColumn>
              </FormRow>
            </>
          )}
        </Form>
      </ModalBody>
      <ModalFooter>
        {!readOnly && (
          <Button icon="save" onClick={onSubmit}>
            {action === 'add' ? 'Add' : 'Save'}
          </Button>
        )}
        <Button variant="outline" icon="slash-circle" onClick={onClose}>
          {readOnly ? 'Close' : 'Cancel'}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

TesterEditModal.propTypes = {
  onClose: PropTypes.any,
  tester: PropTypes.any,
  action: PropTypes.any,
};

export default TesterEditModal;
